// Small helpers shared by the world modules.

import type { DateStr, SourceId } from '../core/schema.ts';

const DAY_MS = 86_400_000;

/** Days since 1970-01-01 for a 'YYYY-MM-DD' date. Integer maths is faster than string compares in hot loops. */
export function dayOf(d: DateStr): number {
  return Math.round(Date.parse(`${d}T00:00:00Z`) / DAY_MS);
}

/** Sources without duplicates, first occurrence order. */
export function uniq(ids: Iterable<SourceId>): SourceId[] {
  return [...new Set(ids)];
}

/** The end date once it has passed by `date`, else null (still in force). */
export function endedBy(to: DateStr | null, date: DateStr): DateStr | null {
  return to !== null && to <= date ? to : null;
}

/** Latest [year, value] with year ≤ `year` in an ascending series, or null. */
export function latestAt(series: ReadonlyArray<readonly [number, number]> | undefined, year: number): [number, number] | null {
  if (!series || series.length === 0 || series[0][0] > year) {
    return null;
  }

  // Binary search for the last entry with entry.year ≤ year.
  let lo = 0;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (series[mid][0] <= year) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return [series[lo][0], series[lo][1]];
}

/** Value at exactly `year`, or null. */
export function valueIn(series: ReadonlyArray<readonly [number, number]> | undefined, year: number): number | null {
  const hit = latestAt(series, year);
  return hit && hit[0] === year ? hit[1] : null;
}

export function pct(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

/** Ascending comparison for date strings (they sort lexically). */
export function byDate(a: DateStr, b: DateStr): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Index of the last element ≤ `x` in an ascending Int32Array, or -1. */
export function lastAtMost(sorted: Int32Array, x: number): number {
  let lo = 0;
  let hi = sorted.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] <= x) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best;
}
